import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { apiService } from '../../../services/api';
import Select from 'react-select';
import CollapseHeader from "../../../core/common/collapse-header";
import { Dropdown } from 'react-bootstrap';
import { OpportunityStatus } from '../../../types/opportunity';
import { Stage } from '../../../types/funnels';
import { useOpportunityDetailHandler } from '../../../hooks/useOpportunityDetailHandler';
import { OpportunityDetailModals } from './components/OpportunityDetailModals';
import EditNoteModal from './components/EditNoteModal';
import { useFunnels } from '../../../hooks/useFunnels';
import { Anotacao, Arquivo, AnotacaoAPI } from '../../../types/notes';
import { Anexo } from '../../../types/anexos';
import { Modal } from 'react-bootstrap';
import { AttachmentModal } from './components/AttachmentModal';
import { EditAttachmentModal } from './components/EditAttachmentModal';
import ModalSemAnexo from './components/ModalSemAnexo';
import { OpportunityDetail } from '../../../types/opportunity';
import { CustomField } from '../../../types/camposPersonalizados';
import handleStageFieldsUpdate from './components/handleStageFieldsUpdate';
import OrcamentoList from './components/OrcamentoList';
import EditFieldModal from './components/EditFieldModal';
import OrcamentoTabs from './components/OrcamentoTabs';
import { API_BASE_URL } from "../../../environment";

const BASE_DOMAIN = API_BASE_URL.replace('/api/v1', '');

const OpportunityDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [opportunity, setOpportunity] = useState<OpportunityDetail | null>(null);
    const [funnelStages, setFunnelStages] = useState<Stage[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const route = all_routes;
    const [showStageModal, setShowStageModal] = useState(false);
    const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
    const [isUpdatingStage, setIsUpdatingStage] = useState(false);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [anotacoes, setAnotacoes] = useState<Anotacao[]>([]);
    const [loadingAnotacoes, setLoadingAnotacoes] = useState(false);
    const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
    const [showDeleteNoteSuccessModal, setShowDeleteNoteSuccessModal] = useState(false);
    const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
    const [isDeletingNote, setIsDeletingNote] = useState(false);
    const [showEditNoteModal, setShowEditNoteModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState<Anotacao | null>(null);
    const [showEditNoteSuccessModal, setShowEditNoteSuccessModal] = useState(false);
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);
    const [anexos, setAnexos] = useState<Anexo[]>([]);
    const [loadingAnexos, setLoadingAnexos] = useState(false);
    const [selectedAttachment, setSelectedAttachment] = useState<Anexo | null>(null);
    const [showEditAttachmentModal, setShowEditAttachmentModal] = useState(false);
    const [showDeleteAttachmentModal, setShowDeleteAttachmentModal] = useState(false);
    const [showDeleteAttachmentSuccessModal, setShowDeleteAttachmentSuccessModal] = useState(false);
    const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);
    const [camposPersonalizados, setCamposPersonalizados] = useState<CustomField[]>([]);
    const [valoresCampos, setValoresCampos] = useState<{ [key: string]: any }>({});
    const [selectedCheckboxField, setSelectedCheckboxField] = useState<{
        id: number;
        titulo: string;
        valor: boolean;
    } | null>(null);
    const [showCheckboxModal, setShowCheckboxModal] = useState(false);
    const [isUpdatingCheckbox, setIsUpdatingCheckbox] = useState(false);
    const [showCheckboxSuccessModal, setShowCheckboxSuccessModal] = useState(false);
    const [showModalSemAnexo, setShowModalSemAnexo] = useState(false);
    const [oportunidadeSemAnexo, setOportunidadeSemAnexo] = useState<string>('');

    // Estados para o multiselect/select
    const [selectedSelectField, setSelectedSelectField] = useState<{
        id: number;
        titulo: string;
        opcoes: any[];
        valorAtual: any;
        novoValor: any;
        tipo_campo: string;
    } | null>(null);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [showSelectOptionsModal, setShowSelectOptionsModal] = useState(false);
    const [isUpdatingSelect, setIsUpdatingSelect] = useState(false);
    const [showSelectSuccessModal, setShowSelectSuccessModal] = useState(false);
    const [selectedField, setSelectedField] = useState<{
        id: number;
        titulo: string;
        tipo_campo: string;
        opcoes?: any[];
        valorAtual: any;
    } | null>(null);
    const [showFieldModal, setShowFieldModal] = useState(false);
    const [isUpdatingField, setIsUpdatingField] = useState(false);
    const [showFieldSuccessModal, setShowFieldSuccessModal] = useState(false);

    const {
        funnels,
        loading: funnelsLoading,
        error: funnelsError,
        selectedFunnel,
        setSelectedFunnel
    } = useFunnels();

    const {
        editingOpportunity,
        isDeleting,
        showSuccessModal,
        showDeleteModal,
        showEditSuccessModal,
        handlePrepareEdit,
        handleUpdate,
        handlePrepareDelete,
        handleDelete,
        setShowDeleteModal,
        setShowSuccessModal,
        setShowEditSuccessModal,
        closeModals
    } = useOpportunityDetailHandler(opportunity);

    // Função para determinar a cor do status
    const getStatusColor = (statusName: string, eFinal?: boolean) => {
        if (eFinal) {
            if (statusName === 'Ganha') return 'bg-success';
            if (statusName === 'Perdida') return 'bg-danger';
            if (statusName === 'Cancelada') return 'bg-secondary';
            return 'bg-info'; // Outros status finais
        }

        switch (statusName?.toLowerCase()) {
            case 'aberta': return 'bg-warning';
            case 'em andamento': return 'bg-warning';
            default: return 'bg-primary';
        }
    };

    // Função para determinar a cor da etapa
    const getStageColor = (stageName: string, currentStage: string) => {
        return stageName === currentStage ? 'bg-primary' : 'bg-light';
    };

    const getFullImageUrl = (relativePath: string | null | undefined): string | null => {
        if (!relativePath) return null;
        if (relativePath.startsWith('http')) return relativePath;
        if (relativePath.startsWith('/')) {
            return `${BASE_DOMAIN}${relativePath}`;
        }
        return `${BASE_DOMAIN}/${relativePath}`;
    };

    const getValorExibicao = (opportunity: any) => {
        return (opportunity.valor_total_proposta !== null &&
            opportunity.valor_total_proposta !== undefined &&
            opportunity.valor_total_proposta > 0)
            ? opportunity.valor_total_proposta
            : opportunity.valor_licitacao
    };

    const handleStageClick = async (stage: Stage) => {
        // Verifica se é a mesma etapa atual
        if (stage.id === opportunity?.id_etapa) {
            return;
        }

        try {
            // Verifica se tem anexos
            const response = await apiService.getAnexos({
                id_oportunidade: opportunity?.id || 0,
                per_page: 1
            });

            // Verificação mais segura do total de anexos
            const total = response?.pagination?.total;
            const temAnexos = response?.success === true &&
                typeof total === 'number' &&
                total > 0;

            if (!temAnexos) {
                // Usa o ModalSemAnexo ao invés do alert
                setOportunidadeSemAnexo(opportunity?.titulo || '');
                setShowModalSemAnexo(true);
                return;
            }

            // Se tem anexos, prossegue com a mudança de etapa
            setSelectedStage(stage);
            setShowStageModal(true);

        } catch (error) {
            console.error('Erro ao verificar anexos:', error);
            alert('Erro ao verificar anexos. Por favor, tente novamente.');
        }
    };

    // Função auxiliar para formatar o tamanho do arquivo
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Função para determinar o ícone baseado no tipo do arquivo
    const getFileIcon = (tipo: string): string => {
        if (tipo.includes('image')) return 'photo';
        if (tipo.includes('pdf')) return 'file-text';
        if (tipo.includes('spreadsheet') || tipo.includes('excel')) return 'table';
        if (tipo.includes('word') || tipo.includes('document')) return 'file-text';
        if (tipo.includes('zip') || tipo.includes('rar')) return 'archive';
        return 'file';
    };

    const handleUpdateStage = async () => {
        if (!selectedStage?.id || !opportunity?.id) {
            console.error('Dados incompletos para atualização');
            return;
        }

        try {
            setIsUpdatingStage(true);
            await apiService.updateOpportunityStage(opportunity.id, selectedStage.id);

            // 2. Atualizar campos personalizados da nova etapa
            await handleStageFieldsUpdate(
                opportunity.id,
                selectedStage.id,
                apiService
            );

            // Atualiza o estado local da oportunidade
            setOpportunity({
                ...opportunity,
                id_etapa: selectedStage.id,
                etapa_nome: selectedStage.nome || ''
            });

            setShowStageModal(false);
        } catch (error) {
            console.error('Erro ao atualizar etapa:', error);
        } finally {
            setIsUpdatingStage(false);
        }
    };

    // Substitua a função formatarValorCampo existente por esta versão atualizada
    const formatarValorCampo = (valor: any, campo: CustomField) => {
        if (valor === undefined || valor === null) return '-';

        switch (campo.tipo_campo) {
            case 'checkbox':
                return (
                    <div className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={valor === "1" || valor === true}
                            onChange={() => handlePrepareEditField(campo, valor)}
                        />
                    </div>
                );

            case 'monetario':
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                        }).format(Number(valor))}
                    </div>
                );

            case 'data':
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        {new Date(valor).toLocaleDateString('pt-BR')}
                    </div>
                );

            case 'porcentagem':
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        {valor}%
                    </div>
                );

            case 'select':
                try {
                    if (campo.opcoes) {
                        const opcoes = typeof campo.opcoes === 'string'
                            ? JSON.parse(campo.opcoes)
                            : campo.opcoes;

                        const valorNormalizado = String(valor).trim();
                        const opcaoSelecionada = opcoes.find((op: any) =>
                            String(op.id) === valorNormalizado ||
                            String(op.value) === valorNormalizado ||
                            String(op.label) === valorNormalizado
                        );

                        const label = opcaoSelecionada ? opcaoSelecionada.label : valor;

                        return (
                            <div
                                className="cursor-pointer"
                                onClick={() => handlePrepareEditField(campo, valor)}
                                style={{ cursor: 'pointer' }}
                            >
                                {valor ? (
                                    <span className="badge bg-primary text-white">{label}</span>
                                ) : (
                                    <span className="text-muted">Clique para selecionar...</span>
                                )}
                            </div>
                        );
                    }
                } catch (error) {
                    console.error('Erro ao processar opções do select:', error);
                }
                return valor;

            case 'multiselect':
                try {
                    let valoresSelecionados = valor;

                    if (typeof valor === 'string') {
                        try {
                            if (valor.startsWith('[') || valor.startsWith('{')) {
                                valoresSelecionados = JSON.parse(valor);
                            } else if (valor.trim() !== '') {
                                valoresSelecionados = [valor];
                            } else {
                                valoresSelecionados = [];
                            }
                        } catch (e) {
                            console.error('Erro ao fazer parse do valor multiselect:', e);
                            valoresSelecionados = [valor];
                        }
                    } else if (!Array.isArray(valoresSelecionados)) {
                        valoresSelecionados = valoresSelecionados ? [valoresSelecionados] : [];
                    }

                    if (Array.isArray(valoresSelecionados)) {
                        if (campo.opcoes) {
                            const opcoes = typeof campo.opcoes === 'string'
                                ? JSON.parse(campo.opcoes)
                                : campo.opcoes;

                            return (
                                <div
                                    className="d-flex flex-wrap gap-1 cursor-pointer"
                                    onClick={() => handlePrepareEditField(campo, valoresSelecionados)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {valoresSelecionados.length > 0 ? (
                                        valoresSelecionados.map((val: any, index: number) => {
                                            const valNormalizado = String(typeof val === 'object' ? (val.id || val.value) : val).trim();
                                            const opcao = opcoes.find((op: any) =>
                                                String(op.id).trim() === valNormalizado ||
                                                String(op.value).trim() === valNormalizado ||
                                                String(op.label).trim() === valNormalizado
                                            );

                                            const label = opcao
                                                ? opcao.label
                                                : (typeof val === 'object' ? (val.label || JSON.stringify(val)) : val.toString());

                                            return (
                                                <span
                                                    key={index}
                                                    className="badge bg-info text-white"
                                                >
                                                    {label}
                                                </span>
                                            );
                                        })
                                    ) : (
                                        <span className="text-muted">Clique para selecionar...</span>
                                    )}
                                </div>
                            );
                        }

                        return valoresSelecionados.map((v: any) =>
                            typeof v === 'object' ? (v.label || JSON.stringify(v)) : v.toString()).join(', ');
                    }
                } catch (error) {
                    console.error('Erro ao processar multiselect:', error, 'Valor:', valor);
                }

                return valor?.toString() || '-';

            case 'email':
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        <span className="text-primary">{valor}</span>
                    </div>
                );

            case 'telefone':
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        {valor}
                    </div>
                );

            case 'cpf':
            case 'cnpj':
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        {valor}
                    </div>
                );

            case 'texto':
            case 'textarea':
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        {valor}
                    </div>
                );

            case 'numero':
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        {valor}
                    </div>
                );

            case 'hora':
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        {valor}
                    </div>
                );

            default:
                return (
                    <div
                        className="cursor-pointer"
                        onClick={() => handlePrepareEditField(campo, valor)}
                        style={{ cursor: 'pointer' }}
                    >
                        {valor}
                    </div>
                );
        }
    };

    const fetchOpportunityDetails = async () => {
        try {
            setLoading(true);
            const response = await apiService.getOpportunityById(Number(id));

            if (response.status && response.data.length > 0) {
                const opportunityData = response.data[0];
                const formattedOpportunity: OpportunityDetail = {
                    ...opportunityData,
                    municipio_nome: opportunityData.municipio_nome,
                };
                setOpportunity(formattedOpportunity);

                // Buscar etapas do funil
                const funnelStagesResponse = await apiService.getEtapasFunil(opportunityData.id_funil);
                setFunnelStages(funnelStagesResponse.data);
            } else {
                setError('Oportunidade não encontrada');
            }
        } catch (err) {
            console.error('Erro detalhado:', err);
            setError(err instanceof Error ? err.message : 'Erro ao carregar detalhes da oportunidade');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        if (id) {
            fetchOpportunityDetails();
        }
    }, [id]);

    const handleUpdateCheckboxValue = async () => {
        if (!selectedCheckboxField) return;

        try {
            setIsUpdatingCheckbox(true);
            const novoValor = !selectedCheckboxField.valor;

            await apiService.updateFieldValues(Number(opportunity?.id), [{
                id_campo_personalizado: selectedCheckboxField.id,
                valor: novoValor ? "1" : "0"
            }]);

            // Atualiza o estado local
            setValoresCampos(prev => ({
                ...prev,
                [selectedCheckboxField.id]: novoValor ? "1" : "0"
            }));

            setShowCheckboxModal(false);
            setShowCheckboxSuccessModal(true);

            // Fecha o modal de sucesso após 2 segundos
            setTimeout(() => {
                setShowCheckboxSuccessModal(false);
            }, 2000);

        } catch (error) {
            console.error('Erro ao atualizar campo:', error);
            alert('Erro ao atualizar o campo. Por favor, tente novamente.');
        } finally {
            setIsUpdatingCheckbox(false);
        }
    };

    const handleUpdateSelectValue = async () => {
        if (!selectedSelectField || !selectedSelectField.novoValor) return;

        try {
            setIsUpdatingSelect(true);

            // Formato diferente para select ou multiselect
            let valorParaSalvar: string | number | null;
            let valorParaEstado: any; // Valor que será usado para atualizar o estado local

            if (selectedSelectField.tipo_campo === 'select') {
                // Para select, salvamos apenas o ID da opção selecionada
                valorParaSalvar = selectedSelectField.novoValor.id || selectedSelectField.novoValor.value || selectedSelectField.novoValor;
                valorParaEstado = valorParaSalvar; // Mantemos o mesmo valor para o estado
            } else {
                // Para multiselect, precisamos preparar os dados para o banco e para o estado local
                const valoresArray = Array.isArray(selectedSelectField.novoValor)
                    ? selectedSelectField.novoValor
                    : [selectedSelectField.novoValor];

                const idsArray = valoresArray.map((opt: any) => opt.id || opt.value || opt);

                // Para o banco, enviamos como string JSON
                valorParaSalvar = JSON.stringify(idsArray);

                // Para o estado local, mantemos como array
                valorParaEstado = idsArray;
            }

            // Faz a chamada para atualizar no banco
            await apiService.updateFieldValues(Number(opportunity?.id), [{
                id_campo_personalizado: selectedSelectField.id,
                valor: valorParaSalvar
            }]);

            // Atualiza o estado local com o valor apropriado
            setValoresCampos(prev => ({
                ...prev,
                [selectedSelectField.id]: valorParaEstado
            }));

            // Fecha o modal e mostra mensagem de sucesso
            setShowSelectModal(false);
            setShowSelectSuccessModal(true);

            // Atualiza os dados da oportunidade para garantir valores corretos
            setTimeout(() => {
                setShowSelectSuccessModal(false);
                // Recarrega os campos personalizados para garantir que estejam atualizados
                if (opportunity?.id_funil && opportunity?.id_etapa && opportunity?.id) {
                    apiService.getFieldValues(opportunity.id)
                        .then(respostaValores => {
                            if (respostaValores.success && respostaValores.data) {
                                const valores = respostaValores.data.reduce((acc, item) => ({
                                    ...acc,
                                    [item.id_campo_personalizado]: item.valor
                                }), {});

                                setValoresCampos(valores);
                            }
                        })
                        .catch(error => {
                            console.error('Erro ao recarregar valores dos campos:', error);
                        });
                }
            }, 2000);

        } catch (error) {
            console.error('Erro ao atualizar campo select:', error);
            alert('Erro ao atualizar o campo. Por favor, tente novamente.');
        } finally {
            setIsUpdatingSelect(false);
        }
    };

    // Adicione as seguintes funções:
    const handlePrepareEditNote = (note: Anotacao) => {
        setSelectedNote(note);
        setShowEditNoteModal(true);
    };

    const handleUpdateNote = async (id: number, formData: FormData) => {
        try {
            await apiService.updateAnotacao(id, {
                titulo: formData.get('titulo') as string,
                observacoes: formData.get('observacoes') as string,
                arquivos: Array.from(formData.getAll('arquivos[]')) as File[],
                remover_arquivos: formData.get('remover_arquivos') ?
                    JSON.parse(formData.get('remover_arquivos') as string) :
                    undefined
            });

            setShowEditNoteModal(false);
            setShowEditNoteSuccessModal(true);

            // Recarregar as anotações após atualização bem-sucedida
            setTimeout(() => {
                fetchAnotacoes();
                setShowEditNoteSuccessModal(false);
            }, 2000);
        } catch (error) {
            console.error('Erro ao atualizar anotação:', error);
        }
    };



    const fetchAnotacoes = async () => {
        if (!id) return;

        try {
            setLoadingAnotacoes(true);
            const response = await apiService.getAnotacoes({
                id_oportunidade: Number(id),
                page: 1,
                per_page: 10
            });

            if (response.success && Array.isArray(response.data)) {
                // Primeiro convertemos para unknown e depois para o tipo desejado
                const anotacoesData = response.data as unknown as AnotacaoAPI[];
                const anotacoesProcessadas: Anotacao[] = anotacoesData.map(note => ({
                    ...note,
                    arquivos: note.arquivos ? JSON.parse(note.arquivos) as Arquivo[] : []
                }));
                setAnotacoes(anotacoesProcessadas);
            }
        } catch (error) {
            console.error('Erro ao carregar anotações:', error);
        } finally {
            setLoadingAnotacoes(false);
        }
    };

    useEffect(() => {
        fetchAnotacoes();
    }, [id]);

    const fetchAnexos = async () => {
        if (!id) return;

        try {
            setLoadingAnexos(true);
            console.log('Buscando anexos para a oportunidade:', id);

            const response = await apiService.getAnexos({
                id_oportunidade: Number(id),
                page: 1,
                per_page: 10
            });


            // Mudando de response.data para response.records
            if (response.success && Array.isArray(response.records)) {
                setAnexos(response.records);
            } else {
                console.log('Nenhum anexo encontrado ou formato inválido');
            }
        } catch (error) {
            console.error('Erro ao carregar anexos:', error);
        } finally {
            setLoadingAnexos(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchAnexos();
        }
    }, [id]);

    useEffect(() => {
        const carregarCamposPersonalizados = async () => {
            if (opportunity?.id_funil && opportunity?.id_etapa) {
                try {
                    // Carrega os campos personalizados da etapa atual
                    const respostaCampos = await apiService.getCustomFields(
                        opportunity.id_funil,
                        opportunity.id_etapa
                    );

                    // Filtra apenas campos com exibir_detalhes true
                    const camposAtivos = respostaCampos.data.filter(campo =>
                        campo.exibir_detalhes == true
                    );

                    setCamposPersonalizados(camposAtivos);

                    // Carrega os valores dos campos para esta oportunidade
                    if (opportunity.id) {
                        const respostaValores = await apiService.getFieldValues(opportunity.id);

                        if (respostaValores.success && respostaValores.data) {
                            const valores = respostaValores.data.reduce((acc, item) => ({
                                ...acc,
                                [item.id_campo_personalizado]: item.valor
                            }), {});

                            setValoresCampos(valores);
                        }
                    }
                } catch (error) {
                    console.error('Erro ao carregar campos personalizados:', error);
                }
            }
        };

        carregarCamposPersonalizados();
    }, [opportunity?.id_funil, opportunity?.id_etapa, opportunity?.id]);



    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="row">
                        <div className="col-12 text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    if (!opportunity) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="alert alert-warning">Oportunidade não encontrada</div>
                </div>
            </div>
        );
    }

    const formatCurrency = (value: number | string) => {
        const numericValue = typeof value === 'string' ? parseFloat(value) : value;
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(numericValue);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };



    // Função para preparar a exclusão da nota
    const handlePrepareDeleteNote = (e: React.MouseEvent, noteId: number) => {
        e.preventDefault();
        setSelectedNoteId(noteId);
        setShowDeleteNoteModal(true);
    };

    // Função para excluir a nota
    const handleDeleteNote = async () => {
        if (!selectedNoteId) return;

        try {
            setIsDeletingNote(true);
            await apiService.deleteAnotacao(selectedNoteId);
            setShowDeleteNoteModal(false);
            setShowDeleteNoteSuccessModal(true);

            // Recarregar as anotações após exclusão bem-sucedida
            setTimeout(() => {
                fetchAnotacoes();
                setShowDeleteNoteSuccessModal(false);
            }, 2000);
        } catch (error) {
            console.error('Erro ao excluir anotação:', error);
        } finally {
            setIsDeletingNote(false);
        }
    };

    // Adicione estas funções
    const handlePrepareEditAttachment = (attachment: Anexo) => {
        setSelectedAttachment(attachment);
        setShowEditAttachmentModal(true);
    };

    const handlePrepareDeleteAttachment = (e: React.MouseEvent, attachmentId: number) => {
        e.preventDefault();
        setSelectedAttachment(anexos.find(a => a.id === attachmentId) || null);
        setShowDeleteAttachmentModal(true);
    };

    const handleDeleteAttachment = async () => {
        if (!selectedAttachment?.id) return;

        try {
            setIsDeletingAttachment(true);
            await apiService.deleteAnexo(selectedAttachment.id);
            setShowDeleteAttachmentModal(false);
            setShowDeleteAttachmentSuccessModal(true);

            setTimeout(() => {
                fetchAnexos();
                setShowDeleteAttachmentSuccessModal(false);
            }, 2000);
        } catch (error) {
            console.error('Erro ao excluir anexo:', error);
            alert('Erro ao excluir o anexo. Por favor, tente novamente.');
        } finally {
            setIsDeletingAttachment(false);
        }
    };

    const handleDownloadAttachment = async (anexo: Anexo) => {
        try {
            await apiService.downloadAnexo(anexo.id, anexo.nome_arquivo);
        } catch (error) {
            console.error('Erro ao fazer download do anexo:', error);
            alert('Erro ao fazer download do anexo. Por favor, tente novamente.');
        }
    };

    // Função genérica para preparar a edição de qualquer campo
    const handlePrepareEditField = (campo: CustomField, valor: any) => {
        let opcoes = [];

        // Processar opções se existirem
        if (campo.opcoes) {
            try {
                opcoes = typeof campo.opcoes === 'string'
                    ? JSON.parse(campo.opcoes)
                    : campo.opcoes;
            } catch (e) {
                console.error('Erro ao processar opções do campo:', e);
                opcoes = [];
            }
        }

        setSelectedField({
            id: campo.id,
            titulo: campo.titulo,
            tipo_campo: campo.tipo_campo,
            opcoes: opcoes,
            valorAtual: valor
        });

        setShowFieldModal(true);
    };

    // Função genérica para atualizar qualquer tipo de campo
    const handleUpdateFieldValue = async (fieldId: number, value: any) => {
        if (!selectedField) return;

        try {
            setIsUpdatingField(true);

            // Preparar o valor para o formato adequado antes de salvar
            let valorParaSalvar: any = value;

            if (selectedField.tipo_campo === 'multiselect' && Array.isArray(value)) {
                valorParaSalvar = JSON.stringify(value);
            } else if (selectedField.tipo_campo === 'checkbox') {
                valorParaSalvar = value === true || value === "1" ? "1" : "0";
            }

            // Chamar a API para atualizar o valor
            await apiService.updateFieldValues(Number(opportunity?.id), [{
                id_campo_personalizado: fieldId,
                valor: valorParaSalvar
            }]);

            // Atualizar o estado local para refletir a mudança na UI
            setValoresCampos(prev => ({
                ...prev,
                [fieldId]: value
            }));

            // Fechar o modal de edição e mostrar confirmação
            setShowFieldModal(false);
            setShowFieldSuccessModal(true);

            // Fechar o modal de sucesso após alguns segundos
            setTimeout(() => {
                setShowFieldSuccessModal(false);

                // Recarregar os valores dos campos caso necessário
                if (opportunity?.id) {
                    apiService.getFieldValues(opportunity.id)
                        .then(respostaValores => {
                            if (respostaValores.success && respostaValores.data) {
                                const valores = respostaValores.data.reduce((acc, item) => ({
                                    ...acc,
                                    [item.id_campo_personalizado]: item.valor
                                }), {});

                                setValoresCampos(valores);
                            }
                        })
                        .catch(error => {
                            console.error('Erro ao recarregar valores dos campos:', error);
                        });
                }
            }, 2000);

        } catch (error) {
            console.error('Erro ao atualizar campo:', error);
            alert('Erro ao atualizar o campo. Por favor, tente novamente.');
        } finally {
            setIsUpdatingField(false);
        }
    };



    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="row">
                    <div className="col-md-12">
                        <div className="page-header">
                            <div className="row align-items-center">
                                <div className="col-8">
                                    <h4 className="page-title">Detalhes da Oportunidade</h4>
                                </div>
                                <div className="col-4 text-end">
                                    <div className="head-icons">
                                        <CollapseHeader />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <div className="contact-head">
                            <div className="row align-items-center">
                                <div className="col-sm-8">
                                    <ul className="contact-breadcrumb">
                                        <li>
                                            <Link to={route.dealsKanban}>
                                                <i className="ti ti-arrow-narrow-left" />
                                                Oportunidades
                                            </Link>
                                        </li>
                                        <li>{opportunity.titulo}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-body pb-2">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <div className="d-flex align-items-center mb-2">

                                        <div>
                                            <h5 className="d-flex gap-1 mb-1">
                                                {opportunity.titulo}
                                                <span className={`badge ${getStatusColor(opportunity.status, opportunity.status_e_final)}`}>
                                                    {opportunity.status}
                                                </span>
                                            </h5>
                                            <p className="mb-1">
                                                <i className="ti ti-building" /> {opportunity.municipio_nome}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2 contacts-action">
                                        <button
                                            className="btn btn-success btn-sm mr-3"
                                            data-bs-toggle="offcanvas"
                                            data-bs-target="#offcanvas_edit"
                                            onClick={handlePrepareEdit}
                                        >
                                            Editar
                                        </button>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={handlePrepareDelete}
                                        >
                                            Excluir
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Barra Lateral */}
                    <div className="col-xl-3 theiaStickySidebar">
                        <div className="card">
                            <div className="card-body p-3">
                                <h6 className="mb-3 fw-semibold">Informações da Oportunidade</h6>
                                <ul>
                                    <li className="row mb-3">
                                        <span className="col-6">Criado em</span>
                                        <span className="col-6">{formatDate(opportunity.data_abertura)}</span>
                                    </li>
                                    <li className="row mb-3">
                                        <span className="col-6">Valor</span>
                                        <span className="col-6">{formatCurrency(getValorExibicao(opportunity))}</span>
                                    </li>
                                    <li className="row mb-3">
                                        <span className="col-6">Data Licitação</span>
                                        <span className="col-6">{formatDate(opportunity.data_licitacao)}</span>
                                    </li>
                                    <li className="row mb-3">
                                        <span className="col-6">Origem</span>
                                        <span className="col-6">{opportunity.origem_nome}</span>
                                    </li>
                                </ul>

                                {camposPersonalizados.length > 0 && (
                                    <>
                                        <hr />
                                        <h6 className="mb-3 fw-semibold">Outras Atividades</h6>
                                        <ul>
                                            {camposPersonalizados.map((campo) => (
                                                <li className="row mb-3" key={campo.id}>
                                                    <span className="col-6">{campo.titulo}</span>
                                                    <span className="col-6">
                                                        {formatarValorCampo(valoresCampos[campo.id], campo)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}

                                <hr />

                                <h6 className="mb-3 fw-semibold">Proprietário</h6>
                                <div className="d-flex align-items-center mb-3">
                                    <div className="avatar avatar-sm avatar-rounded avatar-gray me-3">
                                        <ImageWithBasePath
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(opportunity.dono_nome)}&background=0D8ABC&color=fff`}
                                            alt={opportunity.dono_nome}
                                            className="rounded-circle"
                                        />
                                    </div>
                                    <div>
                                        <p className="mb-0 fw-medium">{opportunity.dono_nome}</p>
                                    </div>
                                </div>

                                <hr />

                                <h6 className="mb-3 fw-semibold">Prioridade</h6>
                                <div className="priority-info">
                                    <div className="dropdown">
                                        <Link to="#" className="dropdown-toggle">
                                            <span>
                                                <i className="ti ti-square-rounded-filled me-1 text-danger circle" />
                                                Alta
                                            </span>
                                        </Link>
                                    </div>
                                </div>

                                <hr />

                                <h6 className="mb-3 fw-semibold">Cliente</h6>
                                <div className="d-flex align-items-center mb-3">
                                    <div>
                                        <p className="mb-0 fw-medium">{opportunity.contato_nome}</p>
                                    </div>
                                </div>

                                <hr />

                                <ul>
                                    <li className="row mb-3">
                                        <span className="col-6">Última Modificação</span>
                                        <span className="col-6">{formatDate(opportunity.data_licitacao)}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Conteúdo Principal */}
                    <div className="col-xl-9">
                        <div className="card mb-3">
                            <div className="card-body pb-0">
                                <h4 className="fw-semibold mb-3">Status do Funil</h4>
                                <div className="pipeline-list">
                                    <ul>
                                        {funnelStages.map((stage, index) => (
                                            <li key={stage.id}>
                                                <Link
                                                    to="#"
                                                    onClick={() => handleStageClick(stage)}
                                                    className={
                                                        stage.nome === opportunity.etapa_nome
                                                            ? 'bg-success'
                                                            : index < funnelStages.findIndex(s => s.nome === opportunity.etapa_nome)
                                                                ? 'bg-info'
                                                                : ''
                                                    }
                                                >
                                                    {stage.nome}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <ul className="nav nav-tabs nav-tabs-bottom" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <Link
                                            to="#"
                                            data-bs-toggle="tab"
                                            data-bs-target="#notes"
                                            className="nav-link active"
                                        >
                                            <i className="ti ti-notes me-1" />
                                            Anotações
                                        </Link>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <Link
                                            to="#"
                                            data-bs-toggle="tab"
                                            data-bs-target="#files"
                                            className="nav-link"
                                        >
                                            <i className="ti ti-file me-1" />
                                            Arquivos
                                        </Link>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <Link
                                            to="#"
                                            data-bs-toggle="tab"
                                            data-bs-target="#orcamento"
                                            className="nav-link"
                                        >
                                            <i className="ti ti-calculator me-1" />
                                            Orçamento
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Conteúdo das Abas */}
                        <div className="tab-content pt-0">
                            <div className="tab-pane fade show active" id="notes">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between">
                                        <h4 className="fw-semibold">Anotações</h4>
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={() => setShowNotesModal(true)}
                                        >
                                            <i className="ti ti-plus"></i> Nova Anotação
                                        </button>
                                    </div>
                                    <div className="card-body">
                                        {loadingAnotacoes ? (
                                            <div className="text-center p-3">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Carregando...</span>
                                                </div>
                                            </div>
                                        ) : anotacoes.length > 0 ? (
                                            <div className="notes-activity">
                                                {anotacoes.map((note) => (
                                                    <div className="card mb-3" key={note.id}>
                                                        <div className="card-body">
                                                            <div className="d-flex align-items-center justify-content-between pb-2">
                                                                <div className="d-inline-flex align-items-center mb-2">
                                                                    <span className="avatar avatar-sm avatar-rounded avatar-gray me-2 flex-shrink-0">
                                                                        <ImageWithBasePath
                                                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(note.usuario_nome)}&background=0D8ABC&color=fff`}
                                                                            alt={note.usuario_nome}
                                                                        />
                                                                    </span>
                                                                    <div>
                                                                        <h6 className="fw-medium mb-1">
                                                                            {note.usuario_nome}
                                                                        </h6>
                                                                        <p className="mb-0">
                                                                            {new Date(note.data_criacao).toLocaleDateString('pt-BR', {
                                                                                day: '2-digit',
                                                                                month: 'short',
                                                                                year: 'numeric',
                                                                                hour: '2-digit',
                                                                                minute: '2-digit'
                                                                            })}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="mb-2">
                                                                    <div className="d-flex gap-2 contacts-action">
                                                                        <Link
                                                                            to="#"
                                                                            className=""
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                handlePrepareEditNote(note);
                                                                            }}
                                                                        >
                                                                            <i className="ti ti-edit text-blue"></i> Editar
                                                                        </Link>
                                                                        <Link
                                                                            to="#"
                                                                            className=""
                                                                            onClick={(e) => handlePrepareDeleteNote(e, note.id)}
                                                                        >
                                                                            <i className="ti ti-trash text-danger"></i> Excluir
                                                                        </Link>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <h5 className="fw-medium mb-1">{note.titulo}</h5>
                                                            <p style={{ whiteSpace: 'pre-line' }}>{note.observacoes}</p>


                                                            {note.arquivos && note.arquivos.length > 0 && (
                                                                <div className="d-inline-flex align-items-center flex-wrap">
                                                                    {note.arquivos.map((arquivo, index) => (
                                                                        <div className="note-download me-3" key={index}>
                                                                            <div className="note-info">
                                                                                <span className="note-icon bg-secondary-success">
                                                                                    <i className={`ti ti-${getFileIcon(arquivo.tipo)}`} />
                                                                                </span>
                                                                                <div>
                                                                                    <h6 className="fw-medium mb-1">
                                                                                        {arquivo.nome_original}
                                                                                    </h6>
                                                                                    <p>{formatFileSize(arquivo.tamanho)}</p>
                                                                                </div>
                                                                            </div>
                                                                            <Link
                                                                                to="#"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    apiService.downloadArquivoAnotacao(
                                                                                        note.id,
                                                                                        arquivo.nome_salvo
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <i className="ti ti-arrow-down" />
                                                                            </Link>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p>Sem anotações registradas</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="tab-pane fade" id="files">

                                <div className="tab-pane fade show active" id="notes">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between">
                                            <h4 className="fw-semibold">Arquivos</h4>
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => setShowAttachmentModal(true)}
                                            >
                                                <i className="ti ti-plus"></i> Novo Arquivo
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            {loadingAnexos ? (
                                                <div className="text-center p-3">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Carregando...</span>
                                                    </div>
                                                </div>
                                            ) : anexos.length > 0 ? (
                                                <div className="files-activity">

                                                    {anexos.map((anexo) => (
                                                        <div className="card border shadow-none mb-3" key={anexo.id}>
                                                            <div className="card-body pb-0">
                                                                <div className="row align-items-center">
                                                                    <div className="col-md-8">
                                                                        <div className="mb-3">
                                                                            <h4 className="fw-medium mb-1">
                                                                                {anexo.titulo}
                                                                            </h4>
                                                                            <p>{anexo.descricao || 'Sem descrição'}</p>
                                                                            <div className="d-flex align-items-center">
                                                                                <span className="avatar avatar-sm avatar-gray avatar-rounded me-2 flex-shrink-0">
                                                                                    <ImageWithBasePath
                                                                                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(anexo.usuario_nome || '')}&background=0D8ABC&color=fff`}
                                                                                        alt={anexo.usuario_nome}
                                                                                        className="rounded-circle"
                                                                                    />
                                                                                </span>
                                                                                <div>
                                                                                    <span className="fs-12">Proprietário</span>
                                                                                    <p>{anexo.usuario_nome}</p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-4 text-md-end">
                                                                        <div className="mb-3">
                                                                            <div className="d-flex gap-2 contacts-action justify-content-end">
                                                                                <Link
                                                                                    to="#"
                                                                                    className=""
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        handlePrepareEditAttachment(anexo);
                                                                                    }}
                                                                                >
                                                                                    <i className="ti ti-edit text-blue"></i> Editar
                                                                                </Link>
                                                                                <Link
                                                                                    to="#"
                                                                                    className=""
                                                                                    onClick={(e) => handlePrepareDeleteAttachment(e, anexo.id)}
                                                                                >
                                                                                    <i className="ti ti-trash text-danger"></i> Excluir
                                                                                </Link>
                                                                                <Link
                                                                                    to="#"
                                                                                    className=""
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        handleDownloadAttachment(anexo);
                                                                                    }}
                                                                                >
                                                                                    <i className="ti ti-download text-info"></i> Download
                                                                                </Link>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p>Sem arquivos anexados</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="tab-pane fade" id="orcamento">
                                <OrcamentoTabs
                                    opportunityId={opportunity.id}
                                    etapaAtual={opportunity.id_etapa}
                                    funilAtual={opportunity.id_funil}
                                    formatCurrency={formatCurrency}
                                    onUpdateComplete={() => {
                                        // Recarrega os detalhes da oportunidade para atualizar valores
                                        fetchOpportunityDetails();
                                    }}
                                />
                            </div>

                        </div>
                        <Modal
                            show={showStageModal}
                            onHide={() => setShowStageModal(false)}
                            centered
                            backdrop="static"
                            keyboard={false}
                        >
                            <Modal.Body>
                                <div className="text-center">
                                    <div className="avatar avatar-xl bg-primary-light rounded-circle mb-3">
                                        <i className="ti ti-arrow-forward fs-36 text-primary" />
                                    </div>
                                    <h4 className="mb-2">Alterar Etapa</h4>
                                    <p className="mb-0">
                                        Deseja mover esta oportunidade para a etapa "{selectedStage?.nome}"?
                                    </p>
                                    <div className="d-flex align-items-center justify-content-center mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-light me-2"
                                            onClick={() => setShowStageModal(false)}
                                            disabled={isUpdatingStage}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleUpdateStage}
                                            disabled={isUpdatingStage}
                                        >
                                            {isUpdatingStage ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                    Atualizando...
                                                </>
                                            ) : (
                                                'Sim, Mover'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>

                        <ModalSemAnexo
                            show={showModalSemAnexo}
                            onHide={() => setShowModalSemAnexo(false)}
                            tituloOportunidade={oportunidadeSemAnexo}
                        />

                        <OpportunityDetailModals
                            editingOpportunity={editingOpportunity}
                            isDeleting={isDeleting}
                            showSuccessModal={showSuccessModal}
                            showDeleteModal={showDeleteModal}
                            showEditSuccessModal={showEditSuccessModal}
                            opportunityId={Number(id)}
                            showNotesModal={showNotesModal}
                            setShowNotesModal={setShowNotesModal}
                            onNoteCreated={fetchAnotacoes}
                            funnels={funnels}
                            selectedFunnel={selectedFunnel}
                            setSelectedFunnel={setSelectedFunnel}
                            onUpdate={handleUpdate}
                            onDelete={handleDelete}
                            onCloseDelete={() => setShowDeleteModal(false)}
                            onCloseSuccess={() => setShowSuccessModal(false)}
                            onCloseEditSuccess={() => setShowEditSuccessModal(false)}
                        />

                        <EditNoteModal
                            show={showEditNoteModal}
                            onHide={() => setShowEditNoteModal(false)}
                            note={selectedNote}
                            onSave={handleUpdateNote}
                        />

                        {/* Modal de Confirmação de Exclusão da Nota */}
                        <Modal
                            show={showDeleteNoteModal}
                            onHide={() => setShowDeleteNoteModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="text-center">
                                    <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                                        <i className="ti ti-trash-x fs-36 text-danger" />
                                    </div>
                                    <h4 className="mb-2">Remover Anotação?</h4>
                                    <p className="mb-0">
                                        Tem certeza que deseja remover esta anotação?
                                        Esta ação não poderá ser desfeita.
                                    </p>
                                    <div className="d-flex align-items-center justify-content-center mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-light me-2"
                                            onClick={() => setShowDeleteNoteModal(false)}
                                            disabled={isDeletingNote}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={handleDeleteNote}
                                            disabled={isDeletingNote}
                                        >
                                            {isDeletingNote ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Excluindo...
                                                </>
                                            ) : (
                                                'Sim, Excluir'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>

                        {/* Modal de Sucesso após Exclusão da Nota */}
                        <Modal
                            show={showDeleteNoteSuccessModal}
                            onHide={() => setShowDeleteNoteSuccessModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="success-message text-center">
                                    <div className="success-popup-icon bg-success-light">
                                        <i className="ti ti-check-check fs-36 text-success" />
                                    </div>
                                    <h3>Anotação Excluída com Sucesso!</h3>
                                    <p>A anotação foi removida permanentemente do sistema.</p>
                                </div>
                            </Modal.Body>
                        </Modal>

                        <Modal
                            show={showEditNoteSuccessModal}
                            onHide={() => setShowEditNoteSuccessModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="success-message text-center">
                                    <div className="success-popup-icon bg-success-light">
                                        <i className="ti ti-check-check fs-36 text-success" />
                                    </div>
                                    <h3>Anotação Atualizada com Sucesso!</h3>
                                    <p>As alterações foram salvas com sucesso.</p>
                                </div>
                            </Modal.Body>
                        </Modal>

                        <AttachmentModal
                            showAttachmentModal={showAttachmentModal}
                            setShowAttachmentModal={setShowAttachmentModal}
                            opportunityId={Number(id)}
                            onAttachmentCreated={fetchAnexos}
                        />

                        {/* Modal de Edição de Anexo */}
                        <EditAttachmentModal
                            show={showEditAttachmentModal}
                            onHide={() => setShowEditAttachmentModal(false)}
                            attachment={selectedAttachment}
                            onSave={async (id, data) => {
                                try {
                                    await apiService.updateAnexo(id, data);
                                    setShowEditAttachmentModal(false);
                                    fetchAnexos();
                                } catch (error) {
                                    console.error('Erro ao atualizar anexo:', error);
                                    alert('Erro ao atualizar o anexo. Por favor, tente novamente.');
                                }
                            }}
                        />

                        {/* Modal de Confirmação de Alteração do Checkbox */}
                        <Modal
                            show={showCheckboxModal}
                            onHide={() => setShowCheckboxModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="text-center">
                                    <div className="avatar avatar-xl bg-primary-light rounded-circle mb-3">
                                        <i className="ti ti-question-mark fs-36 text-primary" />
                                    </div>
                                    <h4 className="mb-2">Alterar Status?</h4>
                                    <p className="mb-0">
                                        Deseja alterar o status do campo "{selectedCheckboxField?.titulo}" para{' '}
                                        {selectedCheckboxField?.valor ? 'não' : 'sim'}?
                                    </p>
                                    <div className="d-flex align-items-center justify-content-center mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-light me-2"
                                            onClick={() => setShowCheckboxModal(false)}
                                            disabled={isUpdatingCheckbox}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleUpdateCheckboxValue}
                                            disabled={isUpdatingCheckbox}
                                        >
                                            {isUpdatingCheckbox ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Atualizando...
                                                </>
                                            ) : (
                                                'Sim, Alterar'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>

                        {/* Modal de Sucesso após Alteração do Checkbox */}
                        <Modal
                            show={showCheckboxSuccessModal}
                            onHide={() => setShowCheckboxSuccessModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="success-message text-center">
                                    <div className="success-popup-icon bg-success-light">
                                        <i className="ti ti-check-check fs-36 text-success" />
                                    </div>
                                    <h3>Status Atualizado com Sucesso!</h3>
                                    <p>O status do campo foi atualizado com sucesso.</p>
                                </div>
                            </Modal.Body>
                        </Modal>

                        {/* Modal de Confirmação de Exclusão */}
                        <Modal
                            show={showDeleteAttachmentModal}
                            onHide={() => setShowDeleteAttachmentModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="text-center">
                                    <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                                        <i className="ti ti-trash-x fs-36 text-danger" />
                                    </div>
                                    <h4 className="mb-2">Remover Arquivo?</h4>
                                    <p className="mb-0">
                                        Tem certeza que deseja remover este arquivo?
                                        Esta ação não poderá ser desfeita.
                                    </p>
                                    <div className="d-flex align-items-center justify-content-center mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-light me-2"
                                            onClick={() => setShowDeleteAttachmentModal(false)}
                                            disabled={isDeletingAttachment}
                                        >Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={handleDeleteAttachment}
                                            disabled={isDeletingAttachment}
                                        >
                                            {isDeletingAttachment ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Excluindo...
                                                </>
                                            ) : (
                                                'Sim, Excluir'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>

                        {/* Modal de Sucesso após Exclusão */}
                        <Modal
                            show={showDeleteAttachmentSuccessModal}
                            onHide={() => setShowDeleteAttachmentSuccessModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="success-message text-center">
                                    <div className="success-popup-icon bg-success-light">
                                        <i className="ti ti-check-check fs-36 text-success" />
                                    </div>
                                    <h3>Arquivo Excluído com Sucesso!</h3>
                                    <p>O arquivo foi removido permanentemente do sistema.</p>
                                </div>
                            </Modal.Body>
                        </Modal>

                        {/* Modal para Selecionar Opções */}
                        <Modal
                            show={showSelectOptionsModal}
                            onHide={() => setShowSelectOptionsModal(false)}
                            centered
                            backdrop="static"
                            size="lg"
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    Selecione: {selectedSelectField?.titulo}
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                {selectedSelectField && (
                                    <div className="mb-3">
                                        <Select
                                            options={selectedSelectField.opcoes}
                                            isMulti={selectedSelectField.tipo_campo === 'multiselect'}
                                            placeholder="Selecione as opções..."
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            defaultValue={
                                                selectedSelectField.tipo_campo === 'multiselect'
                                                    ? selectedSelectField.valorAtual?.map((val: any) => {
                                                        // Encontra a opção correspondente ao valor (por id ou value)
                                                        const opcao = selectedSelectField.opcoes.find((op: any) =>
                                                            op.id == val || op.value == val || op.id == val.id || op.value == val.value);

                                                        if (opcao) return opcao;

                                                        // Se não encontrou a opção, cria um objeto temporário
                                                        return {
                                                            id: val.id || val,
                                                            value: val.value || val,
                                                            label: val.label || val.toString()
                                                        };
                                                    }) || []
                                                    : selectedSelectField.opcoes.find((op: any) =>
                                                        op.id == selectedSelectField.valorAtual ||
                                                        op.value == selectedSelectField.valorAtual)
                                            }
                                            onChange={(selected) => {
                                                // Atualiza o novo valor selecionado
                                                setSelectedSelectField({
                                                    ...selectedSelectField,
                                                    novoValor: selected
                                                });
                                            }}
                                        />

                                        <div className="d-flex justify-content-end mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    setShowSelectOptionsModal(false);

                                                    // Verifica se houve mudança
                                                    if (selectedSelectField.novoValor) {
                                                        setShowSelectModal(true);
                                                    }
                                                }}
                                            >
                                                Confirmar
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </Modal.Body>
                        </Modal>

                        {/* Adicione o novo modal genérico de edição de campo */}
                        <EditFieldModal
                            show={showFieldModal}
                            onHide={() => setShowFieldModal(false)}
                            field={selectedField}
                            onSave={handleUpdateFieldValue}
                            isUpdating={isUpdatingField}
                        />

                        {/* Modal de sucesso após edição de campo */}
                        <Modal
                            show={showFieldSuccessModal}
                            onHide={() => setShowFieldSuccessModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="success-message text-center">
                                    <div className="success-popup-icon bg-success-light">
                                        <i className="ti ti-check-check fs-36 text-success" />
                                    </div>
                                    <h3>Campo Atualizado com Sucesso!</h3>
                                    <p>As alterações foram salvas com sucesso.</p>
                                </div>
                            </Modal.Body>
                        </Modal>

                        {/* Modal de Confirmação para Alterar Select/Multiselect */}
                        <Modal
                            show={showSelectModal}
                            onHide={() => setShowSelectModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="text-center">
                                    <div className="avatar avatar-xl bg-primary-light rounded-circle mb-3">
                                        <i className="ti ti-question-mark fs-36 text-primary" />
                                    </div>
                                    <h4 className="mb-2">Alterar {selectedSelectField?.tipo_campo === 'select' ? 'Seleção' : 'Múltipla Seleção'}?</h4>
                                    <p className="mb-0">
                                        Deseja alterar o valor do campo "{selectedSelectField?.titulo}"?
                                    </p>
                                    <div className="d-flex align-items-center justify-content-center mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-light me-2"
                                            onClick={() => setShowSelectModal(false)}
                                            disabled={isUpdatingSelect}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleUpdateSelectValue}
                                            disabled={isUpdatingSelect}
                                        >
                                            {isUpdatingSelect ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Atualizando...
                                                </>
                                            ) : (
                                                'Sim, Alterar'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>

                        {/* Modal de Sucesso após Edição de Select/Multiselect */}
                        <Modal
                            show={showSelectSuccessModal}
                            onHide={() => setShowSelectSuccessModal(false)}
                            centered
                        >
                            <Modal.Body>
                                <div className="success-message text-center">
                                    <div className="success-popup-icon bg-success-light">
                                        <i className="ti ti-check-check fs-36 text-success" />
                                    </div>
                                    <h3>Campo Atualizado com Sucesso!</h3>
                                    <p>As seleções foram atualizadas com sucesso.</p>
                                </div>
                            </Modal.Body>
                        </Modal>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OpportunityDetails;